import { cyrb53 } from '@framework/engine/utils/cyrb53.ts';
import { sortBy } from 'lodash';
import { getCompetenceLayersInfo } from '../../../createMapSchema.ts';
import { type IMapWidget } from '../../../IMapWidget.ts';
import { getIntersectionLayersInfo } from '../../../layers/getIntersectionLayersInfo.ts';

/**
 * Serializza il descrittore di configurazione con le chiavi degli oggetti in
 * ordine alfabetico, così che due configurazioni uguali producano sempre la
 * stessa stringa a prescindere dall'ordine di inserimento delle proprietà.
 */
function serializeConfigDescriptor(descriptor: unknown): string {
  return JSON.stringify(descriptor, (_key, current: unknown) => {
    if (current === null || typeof current !== 'object') return current;
    if (Array.isArray(current)) return current;
    return Object.fromEntries(
      sortBy(Object.entries(current as Record<string, unknown>), 0),
    );
  });
}

/**
 * Calcola l'hash della configurazione che determina il risultato di
 * intersezioni e competenze del widget mappa.
 *
 * Entrano nell'hash solo i parametri che cambiano il calcolo: sorgente
 * dei layer, attributi estratti, tolleranze, limiti, ruolo e proprietà di
 * competenza, buffer delle geometrie. Restano fuori titolo, alias, visibilità
 * e stile, che incidono solo sul render. I layer sono ordinati per `code`
 * così un riordino nel configuratore non invalida i dati salvati.
 *
 * L'hash viene persistito nel form insieme alle tabelle: al riavvio del
 * widget un valore diverso segnala che la configurazione è cambiata dopo
 * l'ultimo calcolo e le tabelle vanno ricalcolate.
 */
export function computeIntersectionsCompetencesConfigHash(
  widget: IMapWidget,
): string {
  const intersections = sortBy(
    getIntersectionLayersInfo(widget).map(({ layer }) => ({
      code: layer.code,
      type: layer.type,
      url: layer.url,
      wfs: layer.wfs,
      cadastralPreset: layer.intersection.cadastralPreset === true,
      attributes: layer.intersection.attributes ?? [],
      areaTolerance: layer.intersection.areaTolerance,
      maxResults: layer.intersection.maxResults,
    })),
    'code',
  );

  const competences = sortBy(
    getCompetenceLayersInfo(widget).map(layer => ({
      code: layer.code,
      type: layer.type,
      url: layer.url,
      wfs: layer.wfs,
      role: layer.competence?.role,
      identifyingPropertyName: layer.competence?.identifyingPropertyName,
      secondaryCompetenceTypeId: layer.competence?.secondaryCompetenceTypeId,
      areaTolerance: layer.competence?.areaTolerance,
    })),
    'code',
  );

  const descriptor = serializeConfigDescriptor({
    bufferDistance: widget.bufferDistance,
    intersections,
    competences,
  });

  // Serve un hash senza collisioni costruibili: una collisione farebbe
  // passare per aggiornate tabelle calcolate con un'altra configurazione.
  // Esadecimale a lunghezza fissa (14 cifre) per confrontarlo e salvarlo.
  return cyrb53(descriptor).toString(16).padStart(14, '0');
}
